import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import AuthPage from './pages/AuthPage';
import DashboardNew from './pages/DashboardNew';
import TaskDetail from './pages/TaskDetail';
import TaskHistory from './pages/TaskHistory';
import CompletedTasks from './pages/CompletedTasks';
import MagicLogin from './pages/MagicLogin';
import ResetPassword from './pages/ResetPassword';
import CompanyManagement from './pages/CompanyManagement';
import TasksAssigned from './pages/TasksAssigned';
import GroupAssignments from './pages/GroupAssignments';
import BundlesPage from './pages/BundlesPage';
import TemplatesPage from './pages/TemplatesPage';
import TeamPage from './pages/TeamPage';
import SettingsPage from './pages/SettingsPage';
import IntakeFormsPage from './pages/IntakeFormsPage';
import PublicIntakeForm from './pages/PublicIntakeForm';
import TeamActivityPage from './pages/TeamActivityPage';
import useActivityTracking from './hooks/useActivityTracking';
import './App.css';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(savedToken);
          } else {
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    // End activity session before logging out
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activity/session/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updatedUserData) => {
    setUser(prev => ({ ...prev, ...updatedUserData }));
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Provider
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'dark';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Activity Tracker Component - tracks user activity
const ActivityTracker = ({ children }) => {
  const { token, user } = useAuth();
  const isAuthenticated = !!token && !!user;
  
  // Initialize activity tracking
  useActivityTracking(token, isAuthenticated);
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ActivityTracker>
            <Routes>
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/magic-login/:token" element={<MagicLogin />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <DashboardNew />
                </ProtectedRoute>
              } />
              <Route path="/assigned" element={
                <ProtectedRoute>
                  <TasksAssigned />
                </ProtectedRoute>
              } />
              <Route path="/group-assignments" element={
                <ProtectedRoute>
                  <GroupAssignments />
                </ProtectedRoute>
              } />
            <Route path="/bundles" element={
              <ProtectedRoute>
                <BundlesPage />
              </ProtectedRoute>
            } />
            <Route path="/templates" element={
              <ProtectedRoute>
                <TemplatesPage />
              </ProtectedRoute>
            } />
            <Route path="/team" element={
              <ProtectedRoute>
                <TeamPage />
              </ProtectedRoute>
            } />
            <Route path="/tasks/:taskId" element={
              <ProtectedRoute>
                <TaskDetail />
              </ProtectedRoute>
            } />
            <Route path="/history" element={
              <ProtectedRoute>
                <TaskHistory />
              </ProtectedRoute>
            } />
            <Route path="/completed" element={
              <ProtectedRoute>
                <CompletedTasks />
              </ProtectedRoute>
            } />
            <Route path="/company" element={
              <ProtectedRoute>
                <CompanyManagement />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/intake-forms" element={
              <ProtectedRoute>
                <IntakeFormsPage />
              </ProtectedRoute>
            } />
            <Route path="/team-activity" element={
              <ProtectedRoute>
                <TeamActivityPage />
              </ProtectedRoute>
            } />
            {/* Public intake form - no auth required */}
            <Route path="/intake/:slug" element={<PublicIntakeForm />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ActivityTracker>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
